/**
 * EdlTemplatesPanel — gestion des templates d'état des lieux (mig 216).
 *
 * Le service technique prépare un template PAR LIEU : la liste des points à
 * vérifier. Ensuite, sur le terrain, on crée un état des lieux depuis le
 * template (les points sortent pré-remplis). Manager+ uniquement.
 */
import { useEffect, useState } from 'react';
import { Card, Button, Alert } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api, ApiException } from '@/lib/api';
import { Plus, Trash2, ClipboardList, Pencil, FileSpreadsheet, FileText, Upload, Download, Copy } from 'lucide-react';
import type { EdlTemplate, LieuDTO } from '@/lib/api';

type PointDraft = { id: string; libelle: string; piece?: string | null };
type Draft = { id?: string; lieuId: string; nom: string; points: PointDraft[]; actif: boolean; estType: boolean };
/** Chantier 7 (mig 320) — pièces suggérées ; on peut en saisir d'autres. */
const PIECES = ['Général', 'Hall d’entrée', 'Grande salle', 'Cuisine', 'Sanitaires', 'Vestiaires', 'Extérieurs', 'Portes et clés'];

let seq = 0;
const newPointId = () => `tpt-${Date.now()}-${seq++}`;

export function EdlTemplatesPanel() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [templates, setTemplates] = useState<EdlTemplate[]>([]);
  const [lieux, setLieux] = useState<LieuDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  // Chantier 7 — dupliquer un modèle vers un bâtiment (ou en nouveau modèle type).
  const [dupliquerDe, setDupliquerDe] = useState<EdlTemplate | null>(null);
  const [cible, setCible] = useState('');
  async function dupliquer() {
    if (!dupliquerDe) return;
    setError(null);
    try {
      await api.serviceTechnique.edlTemplates.dupliquer(dupliquerDe.id, { lieuId: cible === 'type' ? null : cible });
      setMsg(`« ${dupliquerDe.nom} » dupliqué : la copie est à ajuster.`);
      setDupliquerDe(null); setCible(''); await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Duplication impossible'); }
  }

  async function charger() {
    setError(null);
    try {
      const [tpls, lx] = await Promise.all([
        api.serviceTechnique.edlTemplates.list(true),
        api.admin.lieux.list(),
      ]);
      setTemplates(tpls);
      setLieux(lx.lieux);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { void charger(); }, []);

  // Lieux déjà pris par un template (hors template en cours d'édition).
  const lieuxPris = new Set(templates.filter((t) => t.id !== draft?.id).map((t) => t.lieuId));

  function nouveau() {
    setDraft({ lieuId: '', nom: '', points: [{ id: newPointId(), libelle: '', piece: 'Général' }], actif: true, estType: false });
  }
  function nouveauType() {
    setDraft({ lieuId: '', nom: '', points: [{ id: newPointId(), libelle: '', piece: 'Général' }], actif: true, estType: true });
  }
  function editer(t: EdlTemplate) {
    setDraft({ id: t.id, lieuId: t.lieuId ?? '', nom: t.nom, points: t.points.length ? t.points : [{ id: newPointId(), libelle: '', piece: 'Général' }], actif: t.actif, estType: !!t.estType });
  }

  async function enregistrer() {
    if (!draft) return;
    if (!draft.estType && !draft.lieuId) { setError('Choisissez un bâtiment (ou cochez « Modèle type »)'); return; }
    if (draft.nom.trim().length < 3) { setError('Donnez un nom au template'); return; }
    const points = draft.points.filter((p) => p.libelle.trim() !== '');
    if (points.length === 0) { setError('Ajoutez au moins un point'); return; }
    setSaving(true); setError(null);
    const body = { lieuId: draft.estType ? null : draft.lieuId, estType: draft.estType, nom: draft.nom.trim(),
      points: points.map((p) => ({ ...p, piece: p.piece?.trim() || null })), actif: draft.actif };
    try {
      if (draft.id) await api.serviceTechnique.edlTemplates.update(draft.id, body);
      else await api.serviceTechnique.edlTemplates.create(body);
      setMsg(draft.id ? 'Template mis à jour.' : 'Template créé.');
      setDraft(null); await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setSaving(false); }
  }

  async function supprimer(t: EdlTemplate) {
    const ok = await confirm({ title: 'Supprimer ce template ?', description: `« ${t.nom} » sera retiré. Les états des lieux déjà créés ne sont pas touchés.`, confirmLabel: 'Supprimer', danger: true });
    if (!ok) return;
    try { await api.serviceTechnique.edlTemplates.remove(t.id); setMsg('Template supprimé.'); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  function slugNom(s: string) {
    return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'template';
  }
  async function exporter(t: EdlTemplate, fmt: 'xlsx' | 'pdf' | 'docx') {
    setError(null);
    try {
      if (fmt === 'docx') await api.serviceTechnique.edlTemplates.exportWord(t.id, slugNom(t.nom));
      else if (fmt === 'xlsx') await api.serviceTechnique.edlTemplates.exportExcel(t.id, slugNom(t.nom));
      else await api.serviceTechnique.edlTemplates.exportPdf(t.id, slugNom(t.nom));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Export impossible'); }
  }

  // Import : lit un Excel de points et les injecte dans le brouillon en cours.
  async function importerPoints(file: File) {
    if (!draft) return;
    setError(null);
    try {
      const { points, ignorees } = await api.serviceTechnique.edlTemplates.importPoints(file);
      if (points.length === 0) { setError('Aucun point exploitable dans le fichier.'); return; }
      setDraft({ ...draft, points: [...draft.points.filter((p) => p.libelle.trim() !== ''), ...points] });
      setMsg(`${points.length} point(s) importé(s)${ignorees ? ` · ${ignorees} ligne(s) ignorée(s)` : ''}.`);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Import impossible'); }
  }

  return (
    <div className="space-y-4">
      {dialogElement}
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded={false}>
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <ClipboardList className="h-4 w-4" aria-hidden="true" /> Modèles d’états des lieux
          </h2>
          <span className="ml-auto flex gap-2">
            <Button variant="glass" size="sm" onClick={nouveauType}><Plus className="h-4 w-4" aria-hidden="true" /> Modèle type</Button>
            <Button variant="primary" size="sm" onClick={nouveau}><Plus className="h-4 w-4" aria-hidden="true" /> Modèle d’un bâtiment</Button>
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                <th className="px-4 py-2.5 font-medium">Nom</th>
                <th className="px-4 py-2.5 font-medium">Lieu</th>
                <th className="px-4 py-2.5 font-medium">Points</th>
                <th className="px-4 py-2.5 font-medium">Actif</th>
                <th className="px-4 py-2.5 text-right"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-ink-subtle">Aucun template. Créez-en un par lieu.</td></tr>}
              {templates.map((t) => (
                <tr key={t.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2.5 text-ink">{t.nom}{t.estType && <span className="ml-2 rounded-r-pill bg-grc/15 px-2 py-0.5 text-2xs font-medium text-grc-fg">Modèle type</span>}</td>
                  <td className="px-4 py-2.5 text-ink-muted">{t.estType ? 'Réutilisable' : (t.lieuNom ?? '—')}</td>
                  <td className="px-4 py-2.5 text-ink-muted">{t.points.length}</td>
                  <td className="px-4 py-2.5">{t.actif ? <span className="text-success">Oui</span> : <span className="text-ink-subtle">Non</span>}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button variant="ghost" size="sm" title="Grille vierge au format Excel, à remplir sur ordinateur" onClick={() => void exporter(t, 'xlsx')}><FileSpreadsheet className="h-4 w-4" /> Excel vierge</Button>
                    <Button variant="ghost" size="sm" title="Grille vierge à imprimer : le modèle PAPIER pour l'état des lieux sur place" onClick={() => void exporter(t, 'pdf')}><FileText className="h-4 w-4" /> Papier (PDF)</Button>
                    <Button variant="ghost" size="sm" title="Grille vierge au format Word, rangée par pièce" onClick={() => void exporter(t, 'docx')}><FileText className="h-4 w-4" aria-hidden="true" /> Word</Button>
                    <Button variant="ghost" size="sm" title="Copier ce modèle vers un bâtiment (copie ajustable)" onClick={() => setDupliquerDe(t)}><Copy className="h-4 w-4" aria-hidden="true" /> Dupliquer</Button>
                    <Button variant="ghost" size="sm" onClick={() => editer(t)} aria-label={`Modifier ${t.nom}`}><Pencil className="h-4 w-4" aria-hidden="true" /></Button>
                    <Button variant="ghost" size="sm" aria-label={`Supprimer le modèle ${t.nom}`} onClick={() => void supprimer(t)}><Trash2 className="h-4 w-4 text-danger" aria-hidden="true" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {draft && (
        <Dialog open onClose={() => setDraft(null)} title={draft.id ? 'Modifier le template' : 'Nouveau template'} size="lg"
          footer={<>
            <Button variant="ghost" onClick={() => setDraft(null)}>Annuler</Button>
            <Button variant="primary" loading={saving} onClick={() => void enregistrer()}>{draft.id ? 'Enregistrer' : 'Créer'}</Button>
          </>}>
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={draft.estType} onChange={(e) => setDraft({ ...draft, estType: e.target.checked, lieuId: e.target.checked ? '' : draft.lieuId })} />
              Modèle type (réutilisable, sans bâtiment : on le duplique ensuite vers chaque bâtiment)
            </label>
            {!draft.estType && <div>
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Bâtiment *</label>
              <select value={draft.lieuId} onChange={(e) => setDraft({ ...draft, lieuId: e.target.value })}
                className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
                <option value="">— Choisir un lieu —</option>
                {lieux.map((l) => (
                  <option key={l.id} value={l.id} disabled={lieuxPris.has(l.id)}>
                    {l.nom}{lieuxPris.has(l.id) ? ' (déjà un template)' : ''}
                  </option>
                ))}
              </select>
            </div>}
            <div>
              <label className="mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Nom du modèle *</label>
              <input value={draft.nom} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} placeholder="Ex : État des lieux — Salle des fêtes" className="h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
            </div>
            <div>
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                <label className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Points à vérifier</label>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => void api.serviceTechnique.edlTemplates.modeleImport()}
                    className="inline-flex items-center gap-1 text-xs text-tech-fg hover:underline">
                    <Download className="h-3.5 w-3.5" /> Modèle Excel
                  </button>
                  <label className="inline-flex cursor-pointer items-center gap-1 text-xs text-tech-fg hover:underline">
                    <Upload className="h-3.5 w-3.5" /> Importer
                    <input type="file" accept=".xlsx,.xls" className="hidden"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) void importerPoints(f); e.currentTarget.value = ''; }} />
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                {draft.points.map((p, i) => (
                  <div key={p.id} className="flex items-center gap-2">
                    <input list="edl-pieces" value={p.piece ?? ''} aria-label={`Pièce du point ${i + 1}`} placeholder="Pièce"
                      onChange={(e) => setDraft({ ...draft, points: draft.points.map((x) => x.id === p.id ? { ...x, piece: e.target.value } : x) })}
                      className="h-9 w-40 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
                    <input value={p.libelle} aria-label={`Point ${i + 1}`} onChange={(e) => setDraft({ ...draft, points: draft.points.map((x) => x.id === p.id ? { ...x, libelle: e.target.value } : x) })}
                      placeholder={`Point ${i + 1} (ex : murs, sol, sanitaires, mobilier…)`}
                      className="h-9 flex-1 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
                    <button type="button" onClick={() => setDraft({ ...draft, points: draft.points.filter((x) => x.id !== p.id) })} className="text-ink-subtle hover:text-danger" aria-label="Retirer">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                <datalist id="edl-pieces">{PIECES.map((x) => <option key={x} value={x} />)}</datalist>
                <button type="button" onClick={() => setDraft({ ...draft, points: [...draft.points, { id: newPointId(), libelle: '', piece: draft.points[draft.points.length - 1]?.piece ?? 'Général' }] })} className="inline-flex items-center gap-1.5 text-sm text-tech-fg hover:underline">
                  <Plus className="h-4 w-4" /> Ajouter un point
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="checkbox" checked={draft.actif} onChange={(e) => setDraft({ ...draft, actif: e.target.checked })} />
              Template actif (proposé lors de la création d'un état des lieux)
            </label>
          </div>
        </Dialog>
      )}
      {dupliquerDe && (
        <Dialog open onClose={() => setDupliquerDe(null)} title={`Dupliquer « ${dupliquerDe.nom} »`}
          footer={<><Button variant="ghost" onClick={() => setDupliquerDe(null)}>Annuler</Button><Button variant="primary" disabled={!cible} onClick={() => void dupliquer()}>Dupliquer</Button></>}>
          <label className="block text-sm text-ink">Vers
            <select value={cible} onChange={(e) => setCible(e.target.value)} className="mt-1 h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              <option value="">— Choisir —</option>
              <option value="type">Un nouveau modèle type</option>
              {lieux.map((l) => <option key={l.id} value={l.id} disabled={lieuxPris.has(l.id)}>{l.nom}{lieuxPris.has(l.id) ? ' (a déjà son modèle)' : ''}</option>)}
            </select>
          </label>
          <p className="mt-2 text-xs text-ink-muted">La copie reprend les pièces et les points ; vous l’ajustez ensuite sans toucher à l’original.</p>
        </Dialog>
      )}
    </div>
  );
}
